import { zodResolver } from '@hookform/resolvers/zod'
import { DialogContent } from '@mui/material'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import CustomDialog from '@/components/feedback/CustomDialog'
import FormDialogActions from '@/components/feedback/FormDialogActions'
import TextFieldController from '@/components/inputs/TextFieldController'
import { useAlertContext } from '@/contexts/AlertContext'
import { useInvalidateLocalPqResourceSetQuery } from '@/features/prequalification/localControl/hooks/useLocalPqResourceSetQuery'
import { useInvalidateLocalPqResourceSetsQuery } from '@/features/prequalification/localControl/hooks/useLocalPqResourceSetsQuery'
import { useUpdateLocalPqResourceSetMutation } from '@/features/prequalification/localControl/hooks/useUpdateLocalPqResourceSetMutation'
import type { RenameResourceSetFormValues } from '@/features/prequalification/localControl/schemas'
import { getRenameResourceSetSchema } from '@/features/prequalification/localControl/schemas'
import { errorHandler } from '@/utils/errorHandler'

type RenameResourceSetDialogProps = {
  resourceSetId: string
  currentName: string
  onClose: () => void
}

/**
 * Renames a resource set from its detail page. The resource set name is the resource set's primary
 * identity, so the field is required; on success both the detail and list queries
 * are invalidated so the new name shows everywhere it is read. The parent mounts
 * this only while renaming, so the form re-initialises from `currentName` each time.
 */
function RenameResourceSetDialog({ resourceSetId, currentName, onClose }: Readonly<RenameResourceSetDialogProps>) {
  const { t } = useTranslation()
  const { pushAlert } = useAlertContext()
  const { updateResourceSet, isPending } = useUpdateLocalPqResourceSetMutation({ resourceSetId })
  const invalidateResourceSet = useInvalidateLocalPqResourceSetQuery()
  const invalidateResourceSets = useInvalidateLocalPqResourceSetsQuery()

  const form = useForm<RenameResourceSetFormValues>({
    values: { name: currentName },
    resolver: zodResolver(getRenameResourceSetSchema(t)),
  })

  async function handleSubmit({ name }: RenameResourceSetFormValues) {
    try {
      await updateResourceSet({ name })
      await Promise.all([invalidateResourceSet(), invalidateResourceSets()])
      pushAlert({ message: t('prequalifications.resource_set.rename.success'), severity: 'success' })
      onClose()
    } catch (err) {
      const error = errorHandler(err, t('prequalifications.resource_set.rename.error'))
      pushAlert({ message: error.message, severity: 'error' })
    }
  }

  return (
    <CustomDialog open size="extraSmall" title={t('prequalifications.resource_set.rename.title')} onClose={onClose}>
      <FormProvider {...form}>
        <form noValidate onSubmit={form.handleSubmit(handleSubmit)}>
          <DialogContent>
            <TextFieldController
              required
              id="resource-set-name"
              label={t('prequalifications.resource_set.rename.name_label')}
              name="name"
            />
          </DialogContent>

          <FormDialogActions isLoading={isPending} onCancel={onClose} />
        </form>
      </FormProvider>
    </CustomDialog>
  )
}

export default RenameResourceSetDialog
